import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cloud, LogOut, Loader2, Play, Users, Home as HomeIcon, Radio, HeartHandshake, Trophy, Sparkles, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

// Fliboe Monsoon — a 7-day text-and-choice survival game about the Nepal storm.
// Everything runs client-side except a tiny leaderboard endpoint.
// Free CC0 video loops from Pixabay/Pexels used as ambient backgrounds.

const TOKEN = "fliboe_monsoon_token";
function bootstrap() {
  try {
    if (!localStorage.getItem(TOKEN) && localStorage.getItem("fliboe_token")) {
      localStorage.setItem(TOKEN, localStorage.getItem("fliboe_token"));
    }
  } catch (_) {}
}
if (typeof window !== "undefined") bootstrap();
const auth = () => { bootstrap(); const t = localStorage.getItem(TOKEN); return t ? { Authorization: `Bearer ${t}` } : {}; };

// Free CC0 ambient loops — no API keys, direct hotlink allowed by license.
// Per-scene assignments below make each moment feel like an FMV game beat,
// not one wallpaper stretched across the whole game.
const VIDEO_LOOPS = {
  rain:      "https://cdn.pixabay.com/video/2020/04/13/36323-410189238_tiny.mp4",
  mountain:  "https://cdn.pixabay.com/video/2019/09/29/27259-364562498_tiny.mp4",
  candle:    "https://cdn.pixabay.com/video/2022/12/07/141864-777811435_tiny.mp4",
  darksky:   "https://cdn.pixabay.com/video/2023/07/24/172232-847393729_tiny.mp4",
  water:     "https://cdn.pixabay.com/video/2020/06/17/41972-431402667_tiny.mp4",
  village:   "https://cdn.pixabay.com/video/2020/11/23/57394-484443661_tiny.mp4",
  helicopter:"https://cdn.pixabay.com/video/2022/11/20/139851-772035533_tiny.mp4",
  storm:     "https://cdn.pixabay.com/video/2020/09/24/50900-461780656_tiny.mp4",
  interior:  "https://cdn.pixabay.com/video/2021/12/29/102687-660914489_tiny.mp4",
  road:      "https://cdn.pixabay.com/video/2021/09/17/88739-604996942_tiny.mp4",
  crowd:     "https://cdn.pixabay.com/video/2019/05/22/23955-338793747_tiny.mp4",
  flag:      "https://cdn.pixabay.com/video/2020/11/12/55926-478862834_tiny.mp4",
  bridge:    "https://cdn.pixabay.com/video/2019/08/09/25889-354369959_tiny.mp4",
};

// Event library — 25 events across the 7-day arc.
// Deltas: food, shelter, family (0-6 members remaining), community (-100..+100)
const EVENTS = [
  { id: "d1_radio", day: 1, tag: "interior",
    text: "Your battery-powered FM crackles. Radio Nepal warns of a monsoon depression stalling over Kathmandu. Rain will hit hard by night.",
    choices: [
      { label: "Stockpile — cash + rice run",   d: { food: 2, shelter: 0, community: -1 } },
      { label: "Reinforce the roof with tarp",  d: { food: 0, shelter: 2, community: 0 } },
      { label: "Ignore. Radio always exaggerates.", d: { food: -1, shelter: -2, community: 1 } },
    ] },
  { id: "d1_neighbor", day: 1, tag: "village",
    text: "Your neighbor Sujata asks if you'll help her carry sandbags. Her husband is away.",
    choices: [
      { label: "Help her all afternoon",           d: { food: -1, shelter: 0, community: 6 } },
      { label: "Send your teenage son to help",    d: { food: 0, shelter: 0, community: 3 } },
      { label: "Say you're busy with your own home", d: { food: 0, shelter: 1, community: -4 } },
    ] },
  { id: "d2_flood", day: 2, tag: "water",
    text: "By dawn the Melamchi is over its banks. Water laps at the road. You have hours before it reaches the lower fields.",
    choices: [
      { label: "Move the cow and goats uphill",   d: { food: 1, shelter: 0, community: 1 } },
      { label: "Bag rice + medicine, leave the rest", d: { food: 2, shelter: -1, community: 0 } },
      { label: "Try to save your grandmother's carved chest",  d: { food: -1, shelter: 0, community: 0, family: 0 } },
    ] },
  { id: "d2_pump", day: 2, tag: "rain",
    text: "The village hand pump is submerged. A queue forms at the temple tank.",
    choices: [
      { label: "Wait your turn",                    d: { food: 0, community: 2 } },
      { label: "Cut ahead — grandmother needs meds", d: { food: 0, community: -4 } },
      { label: "Boil rainwater at home instead",    d: { food: -1, shelter: 0, community: 0 } },
    ] },
  { id: "d3_landslide", day: 3, tag: "mountain",
    text: "The hillside above the trail slips. A boulder blocks the mule path. Two houses are gone. No screams — everyone had already fled.",
    choices: [
      { label: "Join the men digging with hands and shovels",  d: { food: -1, shelter: 0, community: 8 } },
      { label: "Bring your generator to run their radio",       d: { food: 0, shelter: -1, community: 5 } },
      { label: "Stay with your family — the trail is unsafe",   d: { food: 0, shelter: 0, community: -2 } },
    ] },
  { id: "d3_medicine", day: 3, tag: "candle",
    text: "Grandmother's insulin is down to two vials. The nearest pharmacy is a 4-hour hike through mud.",
    choices: [
      { label: "You go. Take the risk.",                    d: { food: -1, shelter: 0, community: 2, family_risk: 1 } },
      { label: "Send your son with a note — he's faster",    d: { food: -1, shelter: 0, community: 1, family_risk: 1 } },
      { label: "Ration what you have and pray",              d: { food: 0, shelter: 0, community: 0, gm_risk: 1 } },
    ] },
  { id: "d3_stranger", day: 3, tag: "road",
    text: "A stranger — thin, coughing, foreign — knocks on your door. Says he's a Swiss trekker separated from his group.",
    choices: [
      { label: "Take him in, share dinner",       d: { food: -2, community: 4 } },
      { label: "Give him a blanket, direct him to the school shelter", d: { food: 0, community: 1 } },
      { label: "Don't open the door",             d: { food: 0, community: -3 } },
    ] },
  { id: "d4_radiobreak", day: 4, tag: "storm",
    text: "The FM signal dies. Static. Then a garbled voice: '...seven confirmed dead in Sindhupalchok...army rescue by helicopter...tomorrow, weather permitting.'",
    choices: [
      { label: "Tell no one — panic will make things worse", d: { community: -1 } },
      { label: "Call a village meeting at the temple",       d: { community: 5, food: -1 } },
      { label: "Pack. Start walking now, alone.",            d: { community: -6, food: -1 } },
    ] },
  { id: "d4_child", day: 4, tag: "interior",
    text: "A neighbor's 6-year-old shows up alone. His parents went to fetch water hours ago. He hasn't eaten.",
    choices: [
      { label: "Feed him. Keep him warm. Wait.",    d: { food: -2, community: 6 } },
      { label: "Send him to the temple shelter",     d: { food: 0, community: 2 } },
      { label: "Turn him away — you can't afford more mouths", d: { food: 0, community: -8 } },
    ] },
  { id: "d4_bridge", day: 4, tag: "bridge",
    text: "The wooden footbridge to the pharmacy village collapses. Two people were on it. One made it back.",
    choices: [
      { label: "Organize a rescue party downriver",  d: { food: -1, community: 5, family_risk: 1 } },
      { label: "Light lanterns on both banks — hope",  d: { community: 2 } },
      { label: "Focus on your own family",             d: { community: -3 } },
    ] },
  { id: "d5_food", day: 5, tag: "candle",
    text: "Rice is running low. Your father-in-law hoards a bag under his bed but won't share.",
    choices: [
      { label: "Confront him publicly at the temple", d: { food: 2, community: -3 } },
      { label: "Quietly take a handful. He won't notice.", d: { food: 1, community: 0 } },
      { label: "Accept — trust breaks families.",     d: { food: 0, community: 2 } },
    ] },
  { id: "d5_army", day: 5, tag: "crowd",
    text: "Nepal Army arrives on foot from the ridge. They're triaging who gets on tomorrow's helicopter. Four seats.",
    choices: [
      { label: "Argue for grandmother — she's diabetic",   d: { community: 3, gm_priority: 1 } },
      { label: "Argue for your two children first",         d: { community: 2, child_priority: 1 } },
      { label: "Say nothing. Let them decide.",              d: { community: 1 } },
    ] },
  { id: "d5_looting", day: 5, tag: "darksky",
    text: "Someone broke into the abandoned Sherpa lodge. Rice, batteries, a radio — all gone. Villagers whisper names.",
    choices: [
      { label: "Report it to the army",                d: { community: -2 } },
      { label: "Stay silent — you saw the man's face",  d: { community: 1 } },
      { label: "Track him yourself. Justice.",          d: { community: 3, family_risk: 1 } },
    ] },
  { id: "d6_helicopter", day: 6, tag: "helicopter",
    text: "The helicopter is confirmed for dawn. Weather holds only until 9 AM. You have 4 seats. Your family is 6.",
    choices: [
      { label: "Children + grandmother. You and spouse walk.",  d: { family: -2, community: 4 } },
      { label: "You + spouse + kids. Leave grandmother.",       d: { family: -1, community: -6, gm_left: 1 } },
      { label: "You + kids only. Spouse + grandmother stay.",   d: { family: -2, community: -3 } },
    ] },
  { id: "d6_promise", day: 6, tag: "rain",
    text: "The Swiss trekker from Day 3 offers to swap seats with your grandmother. His embassy is coming for him.",
    choices: [
      { label: "Accept — grandmother lives",       d: { family: 1, community: 3 } },
      { label: "Refuse — you don't trust foreigners", d: { community: -1 } },
      { label: "Only if he pays your family",       d: { food: 3, community: -4 } },
    ] },
  { id: "d7_dawn", day: 7, tag: "flag",
    text: "The rotors thunder overhead. Wind flattens the wet prayer flags. This is it.",
    choices: [
      { label: "Board with dignity, look forward",   d: { community: 2 } },
      { label: "Turn back, hug your neighbor Sujata", d: { community: 4 } },
      { label: "Shout your grandmother's name into the sky", d: { community: 0 } },
    ] },
];

const ENDINGS = [
  { id: "hero",     min_community: 15, min_family: 4, line: "You walked out with 5 of 6, and every villager still says your name at prayer time. You are the reason Sunkoshi remembers this monsoon." },
  { id: "survivor", min_community: 0,  min_family: 3, line: "You made it out. Not everyone did. The village will need years — and so will you." },
  { id: "cold",     min_community: -20, min_family: 3, line: "You survived. But your name is one your neighbors avoid saying. Some doors will never open for you again." },
  { id: "loss",     min_community: 0,  min_family: 1, line: "The helicopter lifted with your grief in it. You made it. Your family didn't. The Sunkoshi took what it wanted." },
  { id: "stayed",   min_community: 30, min_family: 6, line: "You gave up your seat. You stayed. The rains passed. The rebuilding began. Your children still tell this story." },
];

function pickEnding(stats) {
  for (const e of ENDINGS) {
    if (stats.community >= e.min_community && stats.family >= e.min_family) return e;
  }
  return ENDINGS[ENDINGS.length - 1];
}

export function MonsoonLanding() {
  const [board, setBoard] = useState([]);
  useEffect(() => { api.get("/monsoon/leaderboard").then(r => setBoard(r.data?.leaderboard || [])).catch(() => {}); }, []);
  const isSignedIn = typeof window !== "undefined" && !!localStorage.getItem(TOKEN);

  const logout = () => {
    localStorage.removeItem(TOKEN);
    localStorage.removeItem("fliboe_token"); // full sign-out from Fliboe too
    window.location.href = "/monsoon";
  };

  return (
    <main className="min-h-screen text-white relative overflow-hidden" data-testid="monsoon-landing" style={{
      backgroundColor: "#0a0f1e",
      backgroundImage: [
        "linear-gradient(180deg, rgba(6,10,25,0.35) 0%, rgba(6,10,25,0.70) 55%, rgba(6,10,25,0.98) 100%)",
        "radial-gradient(ellipse at 20% 10%, rgba(56,139,253,0.35), transparent 55%)",
        "radial-gradient(ellipse at 85% 90%, rgba(200,80,50,0.30), transparent 55%)",
        "url('https://images.unsplash.com/photo-1544198365-f5d60b6d8190?w=2000&q=80&auto=format&fit=crop')",
      ].join(", "),
      backgroundSize: "cover, cover, cover, cover",
      backgroundPosition: "center",
      backgroundAttachment: "fixed",
    }}>
      {/* Ambient hero video — layered on top of the still image so both play nice */}
      <video src={VIDEO_LOOPS.storm} autoPlay muted loop playsInline
        className="fixed inset-0 w-full h-full object-cover opacity-40 pointer-events-none mix-blend-screen" data-testid="monsoon-hero-video" />
      <header className="relative z-10 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/monsoon" className="flex items-center gap-2"><Cloud className="w-5 h-5 text-sky-400" /><span className="fl-display font-black tracking-tight">Monsoon <span className="text-sky-400 text-[10px] uppercase tracking-widest ml-1">by Fliboe</span></span></Link>
          <div className="flex gap-2 items-center">
            {isSignedIn
              ? <button onClick={logout} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="monsoon-logout"><LogOut className="w-3.5 h-3.5" /> Log out</button>
              : <>
                  <Link to="/monsoon/login" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="monsoon-signin">Sign in</Link>
                  <Link to="/monsoon/login?mode=signup" className="fl-btn !bg-sky-500 !text-black !py-1.5 !px-3 text-xs" data-testid="monsoon-signup">Get started</Link>
                </>
            }
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-4xl mx-auto px-5 pt-12 pb-10">
        <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-sky-400">// Content warning · fictional survival story inspired by real events</div>
        <h1 className="fl-display text-4xl sm:text-6xl font-black mt-2 tracking-tight">Seven days.<br/>The Himalayan sky opens.</h1>
        <p className="text-slate-300 mt-4 max-w-2xl">You are a Nepali villager. Rain won't stop. Your family is six. The rescue helicopter has four seats. What you save is who you become.</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link to="/monsoon/play" className="fl-btn !bg-sky-500 !text-black" data-testid="monsoon-start"><Play className="w-4 h-4" /> Start your run</Link>
          <a href="https://www.nrcs.org" target="_blank" rel="noreferrer" className="fl-btn ghost" data-testid="monsoon-donate"><HeartHandshake className="w-4 h-4" /> Donate to Nepal Red Cross <ExternalLink className="w-3 h-3" /></a>
        </div>
        <div className="mt-4 text-xs text-slate-500 fl-mono">All video is CC0 · No API keys · 100% of tips donated to relief · Zero data sold</div>
      </section>

      <section className="relative z-10 max-w-4xl mx-auto px-5 pb-16">
        <div className="fl-mono text-[10px] uppercase tracking-widest text-white/60 mb-3 flex items-center gap-2"><Trophy className="w-3.5 h-3.5 text-sky-400" /> Global leaderboard</div>
        {board.length === 0 ? (
          <div className="text-slate-500 text-sm fl-card p-4">No runs yet — be first.</div>
        ) : (
          <div className="fl-card p-3 bg-white/[0.02] border-white/10">
            {board.slice(0, 10).map((r, i) => (
              <div key={r.run_id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0 text-sm">
                <span className="text-sky-400 font-mono w-6">{i + 1}</span>
                <span className="text-white/90 flex-1 min-w-0 truncate">{r.display || "Anon"}</span>
                <span className="text-white/60 text-xs">{r.family_saved}/6 saved</span>
                <span className="text-sky-300 font-bold w-14 text-right">{r.score}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// GAME LOOP  /monsoon/play
// ────────────────────────────────────────────────────────────
export function MonsoonPlay() {
  const nav = useNavigate();
  const [stats, setStats] = useState({ food: 5, shelter: 5, family: 6, community: 5, day: 1 });
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  const [flags, setFlags] = useState({ gm_risk: 0, family_risk: 0, gm_left: 0 });
  const [ending, setEnding] = useState(null);
  const [saved, setSaved] = useState(false);
  const [typed, setTyped] = useState("");   // typewriter state
  const [showChoices, setShowChoices] = useState(false);

  useEffect(() => {
    const byDay = {};
    EVENTS.forEach(e => { (byDay[e.day] = byDay[e.day] || []).push(e); });
    const chosen = [];
    for (let d = 1; d <= 7; d++) {
      const pool = (byDay[d] || []).slice().sort(() => Math.random() - 0.5);
      chosen.push(...pool.slice(0, d === 7 ? 1 : 2));
    }
    setEvents(chosen);
  }, []);

  const current = events[idx];
  const bgVideo = current ? (VIDEO_LOOPS[current.tag] || VIDEO_LOOPS.rain) : VIDEO_LOOPS.rain;

  // Typewriter effect for scene text — makes it feel cinematic without any external lib.
  useEffect(() => {
    if (!current) return;
    setTyped(""); setShowChoices(false);
    const t = current.text;
    let i = 0;
    const step = () => {
      i += 1;
      setTyped(t.slice(0, i));
      if (i < t.length) setTimeout(step, 22);
      else setTimeout(() => setShowChoices(true), 250);
    };
    const start = setTimeout(step, 350);
    return () => clearTimeout(start);
  }, [current?.id]); // eslint-disable-line

  const skipTyping = () => {
    if (!current) return;
    setTyped(current.text);
    setShowChoices(true);
  };

  const applyChoice = (c) => {
    const d = c.d || {};
    const next = { ...stats };
    if (typeof d.food === "number")     next.food = Math.max(0, next.food + d.food);
    if (typeof d.shelter === "number")  next.shelter = Math.max(0, next.shelter + d.shelter);
    if (typeof d.family === "number")   next.family = Math.max(0, next.family + d.family);
    if (typeof d.community === "number") next.community = Math.max(-100, next.community + d.community);
    const nf = { ...flags };
    if (d.gm_risk) nf.gm_risk += d.gm_risk;
    if (d.family_risk) nf.family_risk += d.family_risk;
    if (d.gm_left) nf.gm_left += d.gm_left;
    setFlags(nf);

    if (d.family_risk && Math.random() < 0.2) next.family = Math.max(0, next.family - 1);
    if (d.gm_risk && Math.random() < 0.35) next.family = Math.max(0, next.family - 1);

    setStats(next);
    if (idx + 1 >= events.length) {
      setEnding(pickEnding(next));
    } else {
      const nextEv = events[idx + 1];
      if (nextEv) next.day = nextEv.day;
      setStats(next);
      setIdx(idx + 1);
    }
  };

  useEffect(() => {
    if (!ending || saved) return;
    setSaved(true);
    api.post("/monsoon/runs", {
      days_survived: stats.day,
      family_saved: stats.family,
      community: stats.community,
      ending_id: ending.id,
      ending_line: ending.line,
    }, { headers: auth() }).catch(() => {});
  }, [ending, saved, stats.day, stats.family, stats.community]);

  const shareText = ending ? `Monsoon · Day ${stats.day} · ${stats.family}/6 saved · "${ending.line.slice(0, 80)}..." — play at ` : "";
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/monsoon` : "";

  if (!current && !ending) return <div className="min-h-screen bg-black grid place-items-center text-white"><Loader2 className="w-6 h-6 animate-spin text-sky-400" /></div>;

  return (
    <main className="min-h-screen relative overflow-hidden bg-black text-white select-none" data-testid="monsoon-play" onClick={skipTyping}>
      {/* Full-bleed video — the whole screen IS the game, letterbox-style */}
      <video key={bgVideo} src={bgVideo} autoPlay muted loop playsInline
        className="fixed inset-0 w-full h-full object-cover monsoon-video-fade" data-testid="monsoon-bg" />
      <div className="fixed inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/90 pointer-events-none" />
      {/* Cinema letterbox bars */}
      <div className="fixed inset-x-0 top-0 h-12 sm:h-16 bg-black z-10 pointer-events-none" />
      <div className="fixed inset-x-0 bottom-0 h-16 sm:h-20 bg-black z-10 pointer-events-none" />

      {/* Stats bar */}
      <header className="relative z-20 pt-1 sm:pt-2 pb-2">
        <div className="max-w-4xl mx-auto px-4 py-2 flex items-center gap-3 flex-wrap text-xs fl-mono">
          <Link to="/monsoon" className="text-sky-300 hover:underline flex items-center gap-1" onClick={e => e.stopPropagation()}><Cloud className="w-3.5 h-3.5" /> Exit</Link>
          <span className="text-white/40">·</span>
          <span data-testid="stat-day">Day <strong className="text-white">{stats.day}</strong>/7</span>
          <span data-testid="stat-food">🍚 {stats.food}</span>
          <span data-testid="stat-shelter">🏠 {stats.shelter}</span>
          <span data-testid="stat-family"><Users className="inline w-3.5 h-3.5" /> {stats.family}/6</span>
          <span data-testid="stat-community"><HeartHandshake className="inline w-3.5 h-3.5" /> {stats.community >= 0 ? `+${stats.community}` : stats.community}</span>
        </div>
      </header>

      <section className="relative z-20 max-w-2xl mx-auto px-5 min-h-[70vh] flex items-end pb-8 sm:pb-12">
        {ending ? (
          <div className="w-full fl-card p-6 bg-black/85 border-sky-400/40 backdrop-blur" data-testid="monsoon-ending" onClick={e => e.stopPropagation()}>
            <div className="fl-mono text-[11px] uppercase tracking-widest text-sky-400">// Ending · {ending.id}</div>
            <p className="fl-display text-xl sm:text-2xl font-bold mt-3 leading-snug">{ending.line}</p>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <div className="text-sm text-slate-300"><strong className="text-white">Family saved:</strong> {stats.family}/6</div>
              <div className="text-sm text-slate-300"><strong className="text-white">Community trust:</strong> {stats.community >= 0 ? `+${stats.community}` : stats.community}</div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <button onClick={() => { setStats({ food: 5, shelter: 5, family: 6, community: 5, day: 1 }); setIdx(0); setFlags({}); setEnding(null); setSaved(false); }}
                className="fl-btn !bg-sky-500 !text-black" data-testid="monsoon-again"><Sparkles className="w-4 h-4" /> Play again</button>
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText + shareUrl)}`} target="_blank" rel="noreferrer" className="fl-btn ghost" data-testid="monsoon-share-x">Share on X</a>
              <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + shareUrl)}`} target="_blank" rel="noreferrer" className="fl-btn ghost" data-testid="monsoon-share-wa">WhatsApp</a>
              <button onClick={() => navigator.clipboard.writeText(shareText + shareUrl)} className="fl-btn ghost" data-testid="monsoon-share-copy">Copy</button>
              <a href="https://www.nrcs.org" target="_blank" rel="noreferrer" className="fl-btn !bg-red-500 !text-white" data-testid="monsoon-donate-end"><HeartHandshake className="w-4 h-4" /> Donate</a>
            </div>
          </div>
        ) : current && (
          <div className="w-full" data-testid="monsoon-scene" onClick={e => e.stopPropagation()}>
            <div className="p-5 rounded-2xl bg-black/60 border border-white/10 backdrop-blur-md">
              <div className="fl-mono text-[11px] uppercase tracking-widest text-sky-300 mb-2 flex items-center gap-2">
                <Radio className="w-3.5 h-3.5" /> Day {current.day}
              </div>
              <p className="text-lg sm:text-xl leading-relaxed text-white/95 min-h-[4.5rem]">
                {typed}
                {typed.length < current.text.length && <span className="inline-block w-2 h-5 bg-sky-400 ml-0.5 align-middle animate-pulse" />}
              </p>
            </div>
            <div className={`mt-4 space-y-2 transition-opacity duration-500 ${showChoices ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
              {current.choices.map((c, i) => (
                <button key={i} onClick={(e) => { e.stopPropagation(); applyChoice(c); }}
                  className="w-full text-left px-4 py-3 rounded-xl border border-white/15 bg-black/60 backdrop-blur hover:bg-sky-500/25 hover:border-sky-400/60 hover:translate-x-1 transition"
                  data-testid={`monsoon-choice-${i}`}>
                  <span className="text-white/95">{c.label}</span>
                </button>
              ))}
            </div>
            {!showChoices && (
              <div className="mt-2 text-center text-[10px] fl-mono uppercase tracking-widest text-white/40">Tap anywhere to skip</div>
            )}
          </div>
        )}
      </section>

      <style>{`
        .monsoon-video-fade { animation: mvFade 900ms ease-out both; }
        @keyframes mvFade { from { opacity: 0; transform: scale(1.06); } to { opacity: 0.9; transform: scale(1); } }
      `}</style>
    </main>
  );
}

// ────────────────────────────────────────────────────────────
// LOGIN / SIGNUP / FORGOT PASSWORD  /monsoon/login
// Reuses /auth/* — same isolation pattern as Showo.
// ────────────────────────────────────────────────────────────
export function MonsoonLogin() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const initial = params.get("mode") === "signup" ? "signup" : "login";
  const [mode, setMode] = useState(initial);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forgotOk, setForgotOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (mode === "forgot") {
      if (!email.trim()) { setErr("Enter your email."); return; }
      setBusy(true);
      try { await api.post("/auth/forgot-password", { email }); setForgotOk(true); }
      catch (ex) { setErr(ex?.response?.data?.detail || "Could not send reset email."); }
      finally { setBusy(false); }
      return;
    }
    if (!email.trim() || password.length < 8) { setErr("Email + 8-char password required."); return; }
    setBusy(true);
    try {
      const path = mode === "signup" ? "/auth/register" : "/auth/login";
      const body = mode === "signup"
        ? { email, password, name: (name || email.split("@")[0] || "Monsoon Player").trim() }
        : { email, password };
      const { data } = await api.post(path, body);
      if (!data.session_token) throw new Error("No token");
      localStorage.setItem(TOKEN, data.session_token);
      navigate("/monsoon/play", { replace: true });
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join(" · ") : (typeof d === "string" ? d : (ex?.message || "Sign-in failed."));
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#050510] text-white grid place-items-center p-5 relative overflow-hidden" data-testid="monsoon-login">
      <video src={VIDEO_LOOPS.rain} autoPlay muted loop playsInline
        className="fixed inset-0 w-full h-full object-cover opacity-30 pointer-events-none" />
      <div className="fixed inset-0 pointer-events-none bg-gradient-to-b from-black/50 via-black/70 to-black" />
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-6">
          <div className="fl-mono text-[11px] uppercase tracking-[0.3em] text-sky-400">// Fliboe Monsoon</div>
          <h1 className="fl-display text-3xl font-black mt-2">
            {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Welcome back"}
          </h1>
        </div>
        <form onSubmit={submit} className="fl-card p-5 space-y-3 bg-black/60 border-sky-400/20">
          {mode === "forgot" && forgotOk ? (
            <div className="text-emerald-300 text-sm text-center py-4" data-testid="monsoon-forgot-ok">
              If <strong>{email}</strong> matches an account, we&apos;ve emailed a reset link.
              <button type="button" onClick={() => { setMode("login"); setForgotOk(false); setErr(""); }} className="block mx-auto mt-4 text-sky-300 text-xs hover:underline">Back to sign in</button>
            </div>
          ) : (
            <>
              {mode === "signup" && (
                <input className="fl-input w-full" placeholder="Name (optional)" value={name} onChange={e => setName(e.target.value)} data-testid="monsoon-name" />
              )}
              <input className="fl-input w-full" placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="monsoon-email" />
              {mode !== "forgot" && (
                <input className="fl-input w-full" placeholder="Password (8+ chars)" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid="monsoon-password" />
              )}
              {err && <div className="text-red-400 text-sm" data-testid="monsoon-err">{err}</div>}
              <button type="submit" disabled={busy} className="fl-btn w-full justify-center !bg-sky-500 !text-black hover:!bg-sky-400" data-testid="monsoon-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {mode === "signup" ? "Create & play" : mode === "forgot" ? "Send reset email" : "Sign in"}
              </button>
              {mode !== "forgot" && (<>
                <div className="flex items-center gap-3 my-1">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-white/40 text-[10px] font-mono uppercase tracking-widest">or</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                <button type="button" onClick={() => {
                  const redirect = window.location.origin + "/monsoon/auth-callback";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
                }} className="w-full py-2.5 rounded-lg bg-white text-black font-semibold hover:bg-white/90 flex items-center justify-center gap-2 transition" data-testid="monsoon-google">
                  <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                  Continue with Google
                </button>
              </>)}
              <div className="flex items-center justify-between gap-3 mt-1">
                <button type="button" onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setErr(""); }} className="text-sky-300 text-xs hover:underline" data-testid="monsoon-toggle">
                  {mode === "signup" ? "Have an account? Sign in" : mode === "forgot" ? "Back to sign in" : "New here? Create account"}
                </button>
                {mode !== "forgot" && (
                  <button type="button" onClick={() => { setMode("forgot"); setErr(""); }} className="text-white/50 text-xs hover:text-sky-300 hover:underline" data-testid="monsoon-forgot">
                    Forgot password?
                  </button>
                )}
              </div>
            </>
          )}
        </form>
        <div className="text-center mt-4">
          <Link to="/monsoon" className="text-white/40 text-xs hover:text-white">← Back to Monsoon</Link>
        </div>
      </div>
    </main>
  );
}

export function MonsoonAuthCallback() {
  const navigate = useNavigate();
  const done = React.useRef(false);
  React.useEffect(() => {
    if (done.current) return; done.current = true;
    (async () => {
      const m = (window.location.hash || "").match(/session_id=([^&]+)/);
      if (!m) { navigate("/monsoon/login", { replace: true }); return; }
      try {
        const { data } = await api.post("/auth/session", { session_id: m[1] });
        if (!data?.session_token) throw new Error("no token");
        localStorage.setItem(TOKEN, data.session_token);
        navigate("/monsoon/play", { replace: true });
      } catch { navigate("/monsoon/login", { replace: true }); }
    })();
  }, [navigate]);
  return <main className="min-h-screen bg-[#050510] text-white grid place-items-center" data-testid="monsoon-auth-cb"><div className="fl-mono text-xs uppercase tracking-widest text-sky-300">// Signing you in…</div></main>;
}

